// frontend/src/pages/tools/DoseCalculatorPage.tsx
import { useEffect, useRef, useState } from 'react';
import {
  Container,
  Title,
  Text,
  Stack,
  Group,
  NumberInput,
  Select,
  Paper,
  Divider,
  Alert,
  TextInput,
  SegmentedControl,
} from '@mantine/core';
import { IconAlertTriangle, IconCalculator, IconInfoCircle } from '@tabler/icons-react';
import { LeadGenBanner } from '@/components/public-dosing/LeadGenBanner';
import { trackEvent } from '@/utils/analytics';

// Common reef tank parameters with their typical units
const REEF_PARAMETERS = [
  { value: 'calcium', label: 'Calcium', unit: 'mg/L' },
  { value: 'magnesium', label: 'Magnesium', unit: 'mg/L' },
  { value: 'alkalinity', label: 'Alkalinity', unit: 'dKH' },
  { value: 'phosphate', label: 'Phosphate', unit: 'mg/L' },
  { value: 'nitrate', label: 'Nitrate', unit: 'mg/L' },
  { value: 'custom', label: 'Custom', unit: '' },
];

const GALLONS_TO_LITERS = 3.78541;

/**
 * Public dosing calculator page.
 *
 * Calculates the volume of a solution required to raise a tank from its
 * current parameter level to a desired target level.
 *
 * Formula:
 *   required_ml = (desired_increase * tank_volume * solution_dose)
 *                 / (concentration_increase * reference_volume)
 *
 * Where:
 *   desired_increase       = target_level - current_level
 *   tank_volume            = user's actual tank volume (liters)
 *   solution_dose          = known dose of solution (ml)
 *   concentration_increase = how much that dose raises the parameter
 *   reference_volume       = volume the solution was tested/rated for (liters)
 */
export function DoseCalculatorPage() {

  // Form state
  const [selectedElement, setSelectedElement] = useState<string | null>(null);
  const [customElementName, setCustomElementName] = useState('');
  const [unit, setUnit] = useState('mg/L');
  const [volumeUnit, setVolumeUnit] = useState<'liters' | 'gallons'>('liters');
  const [tankVolume, setTankVolume] = useState<number | string>('');
  const [currentLevel, setCurrentLevel] = useState<number | string>('');
  const [targetLevel, setTargetLevel] = useState<number | string>('');
  const [solutionDose, setSolutionDose] = useState<number | string>('');
  const [referenceVolume, setReferenceVolume] = useState<number | string>('');
  const [concentrationIncrease, setConcentrationIncrease] = useState<number | string>('');

  const volumeLabel = volumeUnit === 'gallons' ? 'gallons' : 'liters';

  // Derived element label for display
  const elementLabel = selectedElement === 'custom'
    ? (customElementName || 'this parameter')
    : (REEF_PARAMETERS.find((p) => p.value === selectedElement)?.label || 'this parameter');

  // Update unit when element changes
  const handleElementChange = (value: string | null) => {
    setSelectedElement(value);
    if (value && value !== 'custom') {
      const param = REEF_PARAMETERS.find((p) => p.value === value);
      if (param) setUnit(param.unit);
    } else if (value === 'custom') {
      setUnit('');
    }
    trackEvent('select_content', {
      content_type: 'element',
      item_id: value || 'none',
    });
  };

  // Numeric values for calculation
  const numTankVolume = typeof tankVolume === 'number' ? tankVolume : 0;
  const numCurrentLevel = typeof currentLevel === 'number' ? currentLevel : 0;
  const numTargetLevel = typeof targetLevel === 'number' ? targetLevel : 0;
  const numSolutionDose = typeof solutionDose === 'number' ? solutionDose : 0;
  const numReferenceVolume = typeof referenceVolume === 'number' ? referenceVolume : 0;
  const numConcentrationIncrease = typeof concentrationIncrease === 'number' ? concentrationIncrease : 0;

  // Convert volumes to liters for consistent calculation
  const toL = volumeUnit === 'gallons' ? GALLONS_TO_LITERS : 1;
  const tankVolumeL = numTankVolume * toL;
  const referenceVolumeL = numReferenceVolume * toL;

  const desiredIncrease = numTargetLevel - numCurrentLevel;

  const canCalculate =
    numTankVolume > 0 &&
    numSolutionDose > 0 &&
    numReferenceVolume > 0 &&
    numConcentrationIncrease > 0 &&
    desiredIncrease > 0;

  const requiredDose = canCalculate
    ? (desiredIncrease * tankVolumeL * numSolutionDose) /
      (numConcentrationIncrease * referenceVolumeL)
    : null;

  // Warning thresholds
  const percentageIncrease =
    numCurrentLevel > 0 ? (desiredIncrease / numCurrentLevel) * 100 : 0;
  const showWarning = canCalculate && percentageIncrease > 10;
  const showCaution = canCalculate && percentageIncrease > 5 && percentageIncrease <= 10;
  const showZeroBaseline = canCalculate && numCurrentLevel === 0;

  // Track calculation result in GA4
  const prevRequiredDose = useRef<number | null>(null);
  useEffect(() => {
    if (requiredDose !== null && requiredDose !== prevRequiredDose.current) {
      prevRequiredDose.current = requiredDose;
      trackEvent('calculate_dose', {
        element: elementLabel,
        result_ml: requiredDose.toFixed(2),
        percentage_increase: numCurrentLevel > 0 ? percentageIncrease.toFixed(1) : 'from_zero',
        volume_unit: volumeUnit,
      });
    }
  }, [requiredDose, elementLabel, percentageIncrease, numCurrentLevel, volumeUnit]);

  // SEO meta tags (same pattern as DosingCalculatorPage)
  useEffect(() => {
    const pageTitle = 'Free Dosing Calculator | Reef Data Hub';
    const pageDescription =
      'Calculate the exact dose needed to reach your target water parameter levels. Supports calcium, magnesium, alkalinity, and more. Free, no signup required.';

    const sanitizeUrl = (url: string): string => {
      try {
        const parsed = new URL(url);
        if (!['http:', 'https:'].includes(parsed.protocol)) {
          return window.location.origin + '/tools/dose-calculator';
        }
        return parsed.origin + parsed.pathname;
      } catch {
        return window.location.origin + '/tools/dose-calculator';
      }
    };

    const originalTitle = document.title;
    document.title = pageTitle;

    const createdTags: HTMLMetaElement[] = [];

    let metaDesc = document.querySelector(
      'meta[name="description"]',
    ) as HTMLMetaElement | null;
    const originalMetaDescContent = metaDesc?.getAttribute('content') ?? null;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
      createdTags.push(metaDesc);
    }
    metaDesc.setAttribute('content', pageDescription);

    const ogTags: Array<{ property: string; content: string }> = [
      { property: 'og:title', content: pageTitle },
      { property: 'og:description', content: pageDescription },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: sanitizeUrl(window.location.href) },
    ];

    const originalOgValues: Map<string, string | null> = new Map();

    ogTags.forEach(({ property, content }) => {
      let meta = document.querySelector(
        `meta[property="${property}"]`,
      ) as HTMLMetaElement | null;
      originalOgValues.set(property, meta?.getAttribute('content') ?? null);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
        createdTags.push(meta);
      }
      meta.setAttribute('content', content);
    });

    trackEvent('page_view', {
      page_title: 'Public Dose Calculator',
      page_location: window.location.href,
      page_path: '/tools/dose-calculator',
    });

    return () => {
      document.title = originalTitle;
      createdTags.forEach((tag) => {
        if (tag.parentNode) {
          tag.parentNode.removeChild(tag);
        }
      });

      if (originalMetaDescContent !== null) {
        const metaDesc = document.querySelector('meta[name="description"]');
        metaDesc?.setAttribute('content', originalMetaDescContent);
      }

      originalOgValues.forEach((originalValue, property) => {
        if (originalValue !== null) {
          const meta = document.querySelector(`meta[property="${property}"]`);
          meta?.setAttribute('content', originalValue);
        }
      });
    };
  }, []);

  return (
      <Container size="sm" py="xl">
        <Stack gap="lg">
          {/* Lead Gen Banner */}
          <LeadGenBanner />

          {/* Page Header */}
          <div>
            <Title order={1}>Dosing Calculator</Title>
            <Text c="dimmed" mt="xs">
              Calculate the exact dose needed to reach your target water
              parameter levels. No signup required.
            </Text>
          </div>

          {/* Element Selection */}
          <Paper p="md" radius="md" withBorder>
            <Stack gap="md">
              <Text fw={600} size="sm">
                Element
              </Text>
              <Group gap="md" align="flex-end" wrap="wrap">
                <Select
                  label="Parameter"
                  value={selectedElement}
                  onChange={handleElementChange}
                  data={REEF_PARAMETERS.map((p) => ({
                    value: p.value,
                    label: p.label,
                  }))}
                  placeholder="Select a parameter"
                  searchable
                  clearable
                  style={{ flex: 1, maxWidth: 250 }}
                  aria-label="Select water parameter"
                />
                {selectedElement === 'custom' && (
                  <TextInput
                    label="Element name"
                    value={customElementName}
                    onChange={(e) => setCustomElementName(e.target.value)}
                    placeholder="e.g. Boron"
                    style={{ flex: 1, maxWidth: 200 }}
                    aria-label="Custom element name"
                  />
                )}
                <TextInput
                  label="Unit"
                  value={unit}
                  onChange={(e) => setUnit(e.target.value)}
                  style={{ width: 100 }}
                  aria-label="Unit of measurement"
                />
              </Group>
            </Stack>
          </Paper>

          {/* Tank & Current Levels */}
          <Paper p="md" radius="md" withBorder>
            <Stack gap="md">
              <Group justify="space-between" align="center" wrap="wrap">
                <Text fw={600} size="sm">
                  Your Tank
                </Text>
                <SegmentedControl
                  value={volumeUnit}
                  onChange={(val) => setVolumeUnit(val as 'liters' | 'gallons')}
                  data={[
                    { label: 'Liters', value: 'liters' },
                    { label: 'US Gallons', value: 'gallons' },
                  ]}
                  size="xs"
                />
              </Group>
              <NumberInput
                label={`Tank volume (${volumeLabel})`}
                value={tankVolume}
                onChange={setTankVolume}
                min={0}
                step={10}
                decimalScale={1}
                allowNegative={false}
                placeholder={volumeUnit === 'gallons' ? 'e.g. 30' : 'e.g. 100'}
                style={{ maxWidth: 200 }}
                aria-label={`Tank volume in ${volumeLabel}`}
              />
              <Group gap="md" grow wrap="wrap">
                <NumberInput
                  label={`Current level${unit ? ` (${unit})` : ''}`}
                  value={currentLevel}
                  onChange={setCurrentLevel}
                  min={0}
                  decimalScale={2}
                  allowNegative={false}
                  placeholder="e.g. 400"
                  aria-label="Current parameter level"
                />
                <NumberInput
                  label={`Target level${unit ? ` (${unit})` : ''}`}
                  value={targetLevel}
                  onChange={setTargetLevel}
                  min={0}
                  decimalScale={2}
                  allowNegative={false}
                  placeholder="e.g. 450"
                  aria-label="Target parameter level"
                />
              </Group>
            </Stack>
          </Paper>

          {/* Solution Details */}
          <Paper p="md" radius="md" withBorder>
            <Stack gap="md">
              <Text fw={600} size="sm">
                Your Solution
              </Text>
              <Text size="sm" c="dimmed">
                Describe how your solution performs. For example: &quot;10 ml
                raises 100 {volumeLabel} by 20 {unit || 'units'}&quot;
              </Text>
              <Group gap="md" align="flex-end" wrap="wrap">
                <NumberInput
                  label="Dose (ml)"
                  value={solutionDose}
                  onChange={setSolutionDose}
                  min={0}
                  step={1}
                  decimalScale={2}
                  allowNegative={false}
                  placeholder="e.g. 10"
                  style={{ width: 120 }}
                  aria-label="Solution dose in milliliters"
                />
                <Text size="sm" c="dimmed" pb={8}>
                  raises
                </Text>
                <NumberInput
                  label={`Volume (${volumeLabel})`}
                  value={referenceVolume}
                  onChange={setReferenceVolume}
                  min={0}
                  step={10}
                  decimalScale={1}
                  allowNegative={false}
                  placeholder={volumeUnit === 'gallons' ? 'e.g. 30' : 'e.g. 100'}
                  style={{ width: 140 }}
                  aria-label={`Reference volume in ${volumeLabel}`}
                />
                <Text size="sm" c="dimmed" pb={8}>
                  by
                </Text>
                <NumberInput
                  label={`Increase${unit ? ` (${unit})` : ''}`}
                  value={concentrationIncrease}
                  onChange={setConcentrationIncrease}
                  min={0}
                  decimalScale={2}
                  allowNegative={false}
                  placeholder="e.g. 20"
                  style={{ width: 140 }}
                  aria-label="Concentration increase per dose"
                />
              </Group>
            </Stack>
          </Paper>

          {/* Result */}
          {canCalculate && requiredDose !== null && (
            <Paper
              p="lg"
              radius="md"
              withBorder
              style={{
                background:
                  'linear-gradient(135deg, var(--mantine-color-teal-0) 0%, var(--mantine-color-cyan-0) 100%)',
              }}
            >
              <Stack gap="md">
                <Group gap="xs" align="center">
                  <IconCalculator size={24} />
                  <Text fw={600} size="lg">
                    Result
                  </Text>
                </Group>
                <Group gap="xs" align="baseline" wrap="wrap">
                  <Text fw={700} style={{ fontSize: '2rem' }}>
                    {requiredDose.toFixed(2)} ml
                  </Text>
                  <Text size="sm" c="dimmed">
                    required to raise {numTankVolume} {volumeLabel} of{' '}
                    {elementLabel} from {numCurrentLevel} to {numTargetLevel}
                    {unit ? ` ${unit}` : ''}
                  </Text>
                </Group>
                {numCurrentLevel > 0 && (
                  <Text size="sm" c="dimmed">
                    That&apos;s an increase of {desiredIncrease.toFixed(2)}
                    {unit ? ` ${unit}` : ''} ({percentageIncrease.toFixed(1)}%)
                  </Text>
                )}
              </Stack>
            </Paper>
          )}

          {/* Warning for large doses (>10%) */}
          {showWarning && (
            <Alert
              icon={<IconAlertTriangle size={20} />}
              title="Large dose — consider splitting"
              color="orange"
              variant="light"
              radius="md"
            >
              You are trying to increase {elementLabel} by{' '}
              {percentageIncrease.toFixed(1)}%. Large sudden changes can stress
              livestock. Consider spreading this dose across multiple days to
              allow your tank inhabitants to adjust gradually.
            </Alert>
          )}

          {/* Caution for moderate doses (5-10%) */}
          {showCaution && (
            <Alert
              icon={<IconAlertTriangle size={20} />}
              title="Moderate dose"
              color="yellow"
              variant="light"
              radius="md"
            >
              You are increasing {elementLabel} by{' '}
              {percentageIncrease.toFixed(1)}%. While this is within a
              reasonable range, you may want to consider splitting this across
              two doses if your tank is sensitive to changes.
            </Alert>
          )}

          {/* Warning when dosing from zero */}
          {showZeroBaseline && (
            <Alert
              icon={<IconInfoCircle size={20} />}
              title="Dosing from zero"
              color="blue"
              variant="light"
              radius="md"
            >
              Your current level is 0. Please verify your test reading before
              dosing. If your current level is truly zero, consider splitting
              this dose across multiple days.
            </Alert>
          )}

          {/* Validation hint */}
          {typeof targetLevel === 'number' &&
            typeof currentLevel === 'number' &&
            targetLevel > 0 &&
            targetLevel <= currentLevel && (
              <Alert color="blue" variant="light" radius="md">
                Your target level needs to be higher than your current level to
                calculate a dose.
              </Alert>
            )}

          {/* Footer */}
          <Divider my="lg" />
          <Text size="xs" c="dimmed" ta="center">
            This calculator is provided free by Reef Data Hub. Always verify
            doses before adding anything to your tank.
          </Text>
          <Text size="sm" c="dimmed" ta="center" mt="md">
            Reef Data Hub is a modern tool to integrate, track, and analyze data
            from all sources connected to your reef tank.
          </Text>
        </Stack>
      </Container>
  );
}
